import i18n from "@/lib/i18n/i18n";
import {
  type AttachmentErrorCode,
  type AttachmentStatus,
  type Attachment,
  type StreamedFileDelta,
  type TurnStartFileItem,
  categoriseAttachment,
  mapApiAttachmentStatus,
} from "@soulbyte/core";
import type { VoiceMessagePayload } from "@/modules/chat/voice-messages/hooks";
import { fromKnowledgeUploadFileName } from "@/modules/assistants/knowledge/knowledge-sources.paths";
import { nanoid } from "nanoid";

export type LocalAttachment = {
  id: string;
  name: string;
  mime: string;
  sizeBytes: number;
  file: File | null;
  sampleRate: number | null;
  isVoice: boolean;
  uploadId: string | null;
  status: AttachmentStatus;
  progress: number;
  errorMessage: string | null;
  createdAt: number;
};

/** User uploads use `upload_id`; assistant stream file parts use `document_id`. */
export type MessageThreadAttachment = Attachment & {
  document_id?: string;
};

export type AddVoiceAttachmentInput = Pick<
  VoiceMessagePayload,
  "audioBlob" | "mimeType" | "sampleRate"
> & {
  fileName?: string;
};

/**
 * Returns the authenticated API URL for downloading the original attachment bytes.
 */
export function getAttachmentUrl({
  threadId,
  uploadId,
  assistantId,
}: {
  threadId: string;
  uploadId: string;
  assistantId: string;
}) {
  const apiUrl = import.meta.env.VITE_API_URL;
  if (!apiUrl) {
    throw new Error("VITE_API_URL is not set");
  }
  return `${apiUrl}/api/v1/threads/${assistantId}/${threadId}/uploads/${uploadId}/original`;
}

/**
 * Authenticated URL for a context document’s bytes (assistant-generated or RAG media files).
 */
export function getContextDocumentOriginalUrl({
  assistantId,
  documentId,
}: {
  assistantId: string;
  documentId: string;
}) {
  const apiUrl = import.meta.env.VITE_API_URL;
  if (!apiUrl) {
    throw new Error("VITE_API_URL is not set");
  }
  return `${apiUrl}/api/v1/assistants/${assistantId}/context/${documentId}/original`;
}

/**
 * Maps attachment-related server errors to translated, user-facing text.
 */
export function formatErrorMessage(
  code?: string | null,
  message?: string | null,
) {
  const fallback = message?.trim() || i18n.t("common_error");
  const codeMap: Record<AttachmentErrorCode, string> = {
    ATTACHMENT_NOT_FOUND: i18n.t("chat_attachment_error_not_found"),
    ATTACHMENT_FAILED: i18n.t("chat_attachment_error_failed"),
    ATTACHMENT_TIMEOUT: i18n.t("chat_attachment_error_timeout"),
    ATTACHMENT_UNAVAILABLE: i18n.t("chat_attachment_error_unavailable"),
    ATTACHMENT_LOOKUP_FAILED: i18n.t("chat_attachment_error_lookup_failed"),
  };

  if (!code) return fallback;
  return codeMap[code as AttachmentErrorCode] ?? fallback;
}

/**
 * Maps turn.start file items to local AttachmentDTO-like objects for optimistic UI.
 */
export function mapTurnStartItemsToAttachments(
  fileItems: TurnStartFileItem[],
): Attachment[] {
  return fileItems.map((item) => ({
    upload_id: item.upload_id,
    mime: item.mime,
    name: item.name,
    status: "pending",
  }));
}

/**
 * Maps a `response.delta` file item to a message attachment. The upload id
 * rides along when the server sends it so the live file downloads through the
 * same thread route as a reloaded one.
 */
export function mapStreamFileDeltaToAttachment(
  item: Pick<StreamedFileDelta, "upload_id" | "document_id" | "mime" | "name">,
): MessageThreadAttachment {
  return {
    upload_id: item.upload_id,
    document_id: item.document_id,
    mime: item.mime,
    name: item.name,
    status: "completed",
  };
}

/**
 * Strip the `_asset` marker the backend appends to asset-derived filenames so
 * the UI shows the original name. Matches `_asset` (optionally preceded by a
 * dot) whenever it sits before a dot or at the end of the name — so
 * `report_asset.pdf`, `report_asset.v1.pdf`, and `report._asset.pdf` all
 * collapse to the human-readable form. When `_asset` is at the start of the
 * name, the trailing dot is also consumed so `_asset.foo.pdf` → `foo.pdf`.
 * Mid-stem occurrences without a trailing dot (e.g. `report_asset_v2.pdf`)
 * are left untouched.
 */
export function stripAssetSuffix(name: string): string {
  return name.replace(/^_asset\.|\.?_asset(?=\.|$)/g, "");
}

/**
 * readableFileName turns a stored upload name into the one the user should
 * read: the `__sb_relpath__` folder encoding collapses to its last segment and
 * the backend's `_asset` marker goes with it, so
 * `__sb_relpath__qm%2F04_leitung%2F_asset.ha_4_leitung.pdf` reads as
 * `ha_4_leitung.pdf`. Names without either marker come back untouched.
 */
export function readableFileName(name: string): string {
  if (!name.trim()) return name;
  return stripAssetSuffix(fromKnowledgeUploadFileName(name).name);
}

export function mapAttachmentToLocalAttachment(
  attachment: MessageThreadAttachment,
  file: File | null,
): LocalAttachment {
  const mime = attachment.mime ?? "application/octet-stream";
  const status = mapApiAttachmentStatus(attachment.status);
  const uploadId = attachment.upload_id ?? null;
  const documentId = attachment.document_id ?? null;
  const category = categoriseAttachment(mime);

  return {
    id: attachment.upload_id ?? documentId ?? nanoid(),
    name: readableFileName(
      attachment.name || uploadId || documentId || "Attachment",
    ),
    mime,
    file,
    status,
    isVoice: category === "audio",
    uploadId,
    progress: status === "ready" ? 100 : 0,
    sizeBytes: attachment.size_bytes ?? file?.size ?? 0,
    createdAt: attachment.created_at
      ? new Date(attachment.created_at).getTime()
      : 0,
    sampleRate: null,
    errorMessage:
      status === "failed" ? (attachment.error_message ?? null) : null,
  };
}

/**
 * Converts a fetched attachment blob into a File for preview primitives.
 */
export function mapAttachmentBlobToFile(
  attachment: MessageThreadAttachment,
  blob?: Blob | null,
): File | null {
  if (!blob) return null;

  const ref = attachment.upload_id ?? attachment.document_id ?? "attachment";
  const fileName = readableFileName(attachment.name || `attachment-${ref}`);
  const mime = attachment.mime || blob.type || "application/octet-stream";
  return new File([blob], fileName, { type: mime });
}
